import React from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import { LoadingButton } from '@mui/lab';
import { useUpdateTestCaseMutation, useDeleteTestCaseMutation } from './queries';
import styles from "./style.module.scss"
import { useNotification } from 'context/NotificationContext';

const testCaseSchema = Yup.object({
  name: Yup.string().required('Test case name is required').min(1).max(1000),
});

const EditTestCaseModal = ({ 
  open, 
  onClose, 
  testCase, 
  journeyId, 
  onSuccess,
  showDelete = false,
 }) => {
  const showNotification = useNotification();
  const { mutateAsync: updateTestCase, isPending } = useUpdateTestCaseMutation(journeyId);
  const { mutateAsync: deleteTestCase, isPending: isDeletePending } = useDeleteTestCaseMutation();

  const handleSubmit = async (values) => {
    try {
      await updateTestCase({
        id: testCase.id,
        name: values.name,
      });
      if (onSuccess) {
        onSuccess();
      }
      showNotification(
        "Test case name was updated successfully",
        "success",
        ""
      );
      onClose();
    } catch (error) {
      console.error('Failed to rename test case:', error);
      showNotification(
        error.response?.data?.message || "Failed to rename test case",
        "error",
        ""
      );
    }
  };

  const handleDelete = async () => {
    try{
      await deleteTestCase(testCase.id);
      if (onSuccess) {
        onSuccess();
      }
      showNotification(
        "Test case was deleted successfully",
        "success",
        ""
      );
      onClose();
    }
    catch(error){
      console.error('Failed to delete test case:', error);
      showNotification(
        error.response?.data?.message || "Failed to delete test case",
        "error",
        ""
      );
    }
  }

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      fullWidth 
      maxWidth="sm" 
      onClick={(e) => e.stopPropagation()}
      className={styles.modalContainer}
    >
      <DialogTitle className={styles.title}>
        {showDelete ? 'Edit Test Case' : 'Rename Test Case'}
      </DialogTitle>
      <Formik
        initialValues={{
          name: testCase?.name || '',
        }}
        enableReinitialize
        validationSchema={testCaseSchema}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, handleChange, handleBlur }) => (
          <Form>
            <DialogContent className={styles.contentWrapper}>
              <Box mb={2}>
                <Typography mb={2}>
                  Please edit the name of test case
                </Typography>
                <TextField
                  fullWidth
                  id="name"
                  name="name"
                  label="Test Case Name"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                  disabled={isPending}
                />
              </Box>
            </DialogContent>
            <DialogActions className={styles.buttonWrapper}>
              {showDelete && (
                <LoadingButton
                  onClick={handleDelete}
                  loading={isDeletePending}
                  disabled={isPending}
                  loadingIndicator={<CircularProgress color="error" size={16} />}
                  className={styles.deleteButton}
                >
                  {!isDeletePending && <DeleteOutlineOutlinedIcon color='error' />}
                </LoadingButton>
              )}
              <Button 
                className={styles.button}
                onClick={onClose} 
                color="secondary"
                variant="outlined"
              >
                Cancel
              </Button>
              <LoadingButton
                className={styles.button}
                type="submit"
                variant="contained"
                color="secondary"
                loading={isPending}
                loadingIndicator={
                  <CircularProgress size={20} className={styles.loader}/>
                }
                disabled={isPending || Boolean(errors.name)}
              >
                Save
              </LoadingButton>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default EditTestCaseModal;
